import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Descriptions, Flex, Image, Input, Modal, Popconfirm, Row, Space, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { formatCurrency } from 'utils/number';
import { getOrderPaymentMethodLabel } from '../paymentMethodLabel';
import OrderService from 'services/order';

const { TextArea } = Input;
const { Text } = Typography;
const service = new OrderService();

const getLineImage = (line) => {
  const image = line?.variant?.image
    || line?.variant?.images?.[0]
    || line?.product?.thumbnail
    || line?.product?.image
    || line?.product?.images?.[0]
    || null;

  if (!image) return null;
  if (typeof image === 'string') return image;
  return image?.url || null;
};

const OrderDetailDesktop = ({ viewModel }) => {
  const {
    data,
    customer,
    lineItems,
    id,
    navigate,
    setShowOrderPayment,
    setShowOrderHistoryModal,
    setShowOrderNoteModal,
    setIsModalPaymentProofOpen,
    cancelOrder,
    finishOrder,
    createInvoice,
    createPackingList,
    createShippingLabel,
    openQuickStatusEditor,
    loadData,
  } = viewModel;

  const [invoiceEditorOpen, setInvoiceEditorOpen] = useState(false);
  const [invoiceDraft, setInvoiceDraft] = useState({
    company_name: '',
    tax_number: '',
    address: '',
    email: '',
    note: '',
  });

  const shippingStatusTitle = data.shipping_status?.title || data.shipping_status_id || '-';
  const internalStatusId = data.internal_status?.id || data.internal_status_id || data.internal?.status?.id;
  const internalStatusTitle = data.internal_status?.title
    || data.internal?.status?.title
    || data.store_setting?.order_internal_status_map?.[internalStatusId]?.title
    || internalStatusId
    || '-';
  const paymentMethodLabel = getOrderPaymentMethodLabel(data);

  const invoiceInfo = data?.invoice_info || data?.shipping_address?.invoice_info || {};
  const invoiceEnabled = Boolean(data?.store_setting?.invoice_enabled || Number(data?.tax_percent ?? 0) > 0 || Object.values(invoiceInfo || {}).some((value) => {
    if (typeof value === 'string') return value.trim().length > 0;
    return !!value;
  }));

  const taxPercent = Number(data?.tax_percent ?? invoiceInfo?.tax_percent ?? 0);

  const visibleInvoiceInfo = useMemo(() => ({
    company_name: invoiceInfo?.company_name || '',
    tax_number: invoiceInfo?.tax_number || '',
    address: invoiceInfo?.address || '',
    email: invoiceInfo?.email || '',
    note: invoiceInfo?.note || '',
  }), [JSON.stringify(invoiceInfo)]);

  const invoiceSummary = useMemo(() => {
    const parts = [
      invoiceInfo?.company_name,
      invoiceInfo?.tax_number && `MST ${invoiceInfo.tax_number}`,
      taxPercent > 0 ? `Thuế ${taxPercent}%` : null,
      Number(data?.tax_amount ?? 0) > 0 ? formatCurrency(Number(data?.tax_amount ?? 0)) : null,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(' • ') : 'Chưa có thông tin thuế';
  }, [invoiceInfo?.company_name, invoiceInfo?.tax_number, taxPercent, data?.tax_amount]);

  const openInvoiceEditor = () => {
    setInvoiceDraft({ ...visibleInvoiceInfo });
    setInvoiceEditorOpen(true);
  };

  const saveInvoiceInfo = async () => {
    const normalized = {
      company_name: String(invoiceDraft.company_name || '').trim(),
      tax_number: String(invoiceDraft.tax_number || '').trim(),
      address: String(invoiceDraft.address || '').trim(),
      email: String(invoiceDraft.email || '').trim(),
      note: String(invoiceDraft.note || '').trim(),
    };

    const payload = {
      id: data.id,
      invoice_info: Object.values(normalized).some((value) => value.length > 0) ? normalized : null,
    };

    await service.update(payload);
    setInvoiceEditorOpen(false);
    await loadData();
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {data.status == 'canceled' && (
            <Alert
              message="Đơn hàng này đã bị huỷ"
              description={`Lý do huỷ: ${data.cancel?.reason || ''}`}
              type="error"
              closable
            />
          )}

          {data.status == 'finalized' && (
            <Alert
              message="Đơn hàng này đã hoàn tất"
              description={`Ghi chú: ${data.finalized?.note || ''}`}
              type="success"
              closable
            />
          )}

          <Descriptions bordered size="small" column={{ xs: 1, sm: 1, md: 2, lg: 3, xl: 3, xxl: 3 }}>
            <Descriptions.Item label="Mã đơn hàng">{data.id}</Descriptions.Item>
            <Descriptions.Item label="Khách hàng">{customer.full_name}</Descriptions.Item>
            <Descriptions.Item label="SDT">{customer.phone}</Descriptions.Item>
            <Descriptions.Item label="Thanh toán" span={3}>
              <div style={{ wordBreak: 'break-word' }}>{data.payment_status?.title}</div>
            </Descriptions.Item>
            <Descriptions.Item label="Phương thức thanh toán" span={3}>
              <div style={{ wordBreak: 'break-word' }}>{paymentMethodLabel}</div>
            </Descriptions.Item>
            <Descriptions.Item label="Vận chuyển" span={3}>
              <div style={{ wordBreak: 'break-word' }}>{shippingStatusTitle}</div>
            </Descriptions.Item>
            <Descriptions.Item label="Nội bộ" span={3}>
              <div style={{ wordBreak: 'break-word' }}>{internalStatusTitle}</div>
            </Descriptions.Item>

            <Descriptions.Item label="Địa chỉ" span={3}>
              <div style={{ wordBreak: 'break-word' }}>{data.shipping_address?.address}</div>
            </Descriptions.Item>
            <Descriptions.Item label="Ghi Chú" span={3}>
              <div style={{ wordBreak: 'break-word' }}>{data.note}</div>
            </Descriptions.Item>
            {invoiceEnabled && (
              <Descriptions.Item label="Thuế" span={3}>
                <Flex justify="space-between" align="center" gap={8} style={{ width: '100%' }}>
                  <Text style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{invoiceSummary}</Text>
                  <Button type="link" size="small" icon={<EditOutlined />} onClick={openInvoiceEditor}>Sửa</Button>
                </Flex>
              </Descriptions.Item>
            )}
          </Descriptions>

          {data.commission > 0 && (
            <Descriptions bordered size="small" column={{ xs: 1, sm: 1, md: 2, lg: 3, xl: 3, xxl: 3 }}>
              <Descriptions.Item label="CTV" span={3}>{data.collaborator?.full_name || data.ref_code || '-'}</Descriptions.Item>
              <Descriptions.Item label="SĐT CTV" span={3}>{data.collaborator?.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="Hoa hồng" span={3}>{formatCurrency(data.commission)}</Descriptions.Item>
            </Descriptions>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <Space wrap>
                <Popconfirm
                  title="Huỷ đơn hàng"
                  description="Bạn có chắc chắn muốn huỷ đơn hàng này?"
                  onConfirm={cancelOrder}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button danger>Huỷ</Button>
                </Popconfirm>

                <Popconfirm
                  title="Hoàn tất"
                  description="Bạn có chắc chắn muốn hoàn tất đơn hàng này?"
                  onConfirm={finishOrder}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button>Hoàn tất</Button>
                </Popconfirm>

                <Button onClick={() => setShowOrderPayment(true)}>Thanh toán</Button>
                <Button onClick={openQuickStatusEditor}>Cập nhật nhanh trạng thái</Button>
                <Button onClick={() => setShowOrderHistoryModal(true)}>Lịch sử</Button>
                <Button onClick={() => setShowOrderNoteModal(true)}>Ghi chú</Button>
              </Space>
            </div>
            <div style={{ flexGrow: 1, flexShrink: 1, minWidth: 0 }}>
              <Flex justify="flex-end">
                <Space align="end" wrap>
                  <Button type="primary" onClick={() => navigate(`/order/edit/${id}`)}>Sửa</Button>
                  <Button type="primary" onClick={createInvoice}>In Hoá Đơn</Button>
                  {data.payment_proof?.image && (
                    <Button type="primary" onClick={() => setIsModalPaymentProofOpen(true)}>Thông tin thanh toán</Button>
                  )}
                  <Button type="primary" onClick={createPackingList}>In Phiếu Đóng Gói</Button>
                  <Button type="primary" onClick={createShippingLabel}>In Tem Vận Chuyển</Button>
                </Space>
              </Flex>
            </div>
          </div>
        </Space>
      </Card>

      <Row gutter={16}>
        <Col span={16}>
          <Card>
            <table className="tbl_order" style={{ width: '100%' }}>
              <tr>
                <th>Sản phẩm</th>
                <th>Số lượng</th>
                <th>Đơn giá</th>
                <th>Thành tiền</th>
              </tr>

              <tbody className="ant-table-tbody">
                {lineItems.map((line, index) => (
                  <tr key={`${line.product?.id || ''}-${line.variant?.id || ''}-${line.id || ''}`}>
                    <td>
                      <Space size={8}>
                        <span style={{ minWidth: 16, fontWeight: 500 }}>{index + 1}</span>
                        {getLineImage(line) && (
                          <Image
                            src={getLineImage(line)}
                            width={36}
                            height={36}
                            style={{ objectFit: 'cover', borderRadius: 6 }}
                            fallback='data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzNiIgaGVpZ2h0PSIzNiI+PHJlY3Qgd2lkdGg9IjM2IiBoZWlnaHQ9IjM2IiBmaWxsPSIjZjVmNWY1Ii8+PC9zdmc+'
                          />
                        )}
                        <span>
                          <div>{line.product?.name}</div>
                          {line.variant?.variant_title && (
                            <div style={{ fontSize: 12, color: '#888' }}>{line.variant.variant_title}</div>
                          )}
                        </span>
                      </Space>
                    </td>
                    <td>{line.quantity}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(line.price)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(line.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <table style={{ width: '100%' }} className="tbl_order">
              <colgroup>
                <col></col>
                <col style={{ textAlign: 'right' }}></col>
              </colgroup>
              <tbody className="ant-table-tbody">
                <tr>
                  <td>Tiền hàng</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.sub_amount)}</td>
                </tr>
                <tr>
                  <td>Giảm giá</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.discount_amount)}</td>
                </tr>
                <tr>
                  <td>Mã khuyến mãi</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.voucher_amount)}</td>
                </tr>
                <tr>
                  <td>Xu</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.point_discount)}</td>
                </tr>
                <tr>
                  <td>Phí ship</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.shipping_amount)}</td>
                </tr>
                {invoiceEnabled && (
                  <tr>
                    <td>Thuế xuất{taxPercent > 0 ? ` (${taxPercent}%)` : ''}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(Number(data?.tax_amount ?? 0))}</td>
                  </tr>
                )}
                <tr>
                  <td>Tổng tiền</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.total_amount)}</td>
                </tr>
              </tbody>
            </table>
          </Card>
        </Col>
      </Row>

      <Modal
        title="Chỉnh sửa thông tin hoá đơn"
        open={invoiceEditorOpen}
        onCancel={() => setInvoiceEditorOpen(false)}
        onOk={saveInvoiceInfo}
        okText="Lưu"
        cancelText="Huỷ"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <div style={{ marginBottom: 6 }}>Tên công ty</div>
            <Input value={invoiceDraft.company_name} onChange={(e) => setInvoiceDraft((prev) => ({ ...prev, company_name: e.target.value }))} />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>Mã số thuế</div>
            <Input value={invoiceDraft.tax_number} onChange={(e) => setInvoiceDraft((prev) => ({ ...prev, tax_number: e.target.value }))} />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>Địa chỉ hoá đơn</div>
            <Input value={invoiceDraft.address} onChange={(e) => setInvoiceDraft((prev) => ({ ...prev, address: e.target.value }))} />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>Email</div>
            <Input value={invoiceDraft.email} onChange={(e) => setInvoiceDraft((prev) => ({ ...prev, email: e.target.value }))} />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>Ghi chú hoá đơn</div>
            <TextArea rows={3} value={invoiceDraft.note} onChange={(e) => setInvoiceDraft((prev) => ({ ...prev, note: e.target.value }))} />
          </div>
        </Space>
      </Modal>
    </Space>
  );
};

export default OrderDetailDesktop;
